import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

import { useDucatClient } from '@/context/DucatClientCtx';
import { Button } from '@/components/button';
import { Icons } from '@/components/icons';
import { useVaultProfile } from '@/hooks/react/useVaultProfile';

import { getIsQuantaRoutes } from '../utils';

import WalletConnect from './wallet-connect';
import NetworkSettings from './network-settings';
import BitcoinBanner from './bitcoin-banner';
import DataTag from './data-tag';
import Label from './label';
import DropdownNav from './dropdown-nav';
import { getPageName } from './utils';

const TESTNET_GUIDE_TEXT = 'Follow Testnet Guide here.';

const renderErrorMessage = (message: string) => {
  if (message.includes(TESTNET_GUIDE_TEXT)) {
    const parts = message.split(TESTNET_GUIDE_TEXT);

    return (
      <>
        {parts[0]}
        <Link
          href='https://ducatstable.notion.site/How-To-Use-Ducat-Protocol-on-Testnet-137941be6f5e80fbb3acc7e15a53c240'
          target='_blank'
          rel='noopener noreferrer'
          className='underline underline-offset-2 hover:opacity-85'
        >
          {TESTNET_GUIDE_TEXT}
        </Link>
        {parts[1]}
      </>
    );
  }

  return message;
};

const Header = () => {
  const pathname = usePathname();
  const { wallet, connectedNetwork, selectedNetwork, client, walletErrorMessage } = useDucatClient();
  const { vaultProfile, tag } = useVaultProfile();

  const [error, setError] = useState<string | null>(null);
  const [isNetworkOpen, setIsNetworkOpen] = useState<boolean>(false);

  const openNetworkSettings = () => setIsNetworkOpen(true);
  const closeNetworkSettings = () => setIsNetworkOpen(false);

  const isConnected = wallet !== null;
  const hasVault = !!vaultProfile;
  const isQuantaRoutes = getIsQuantaRoutes(pathname);

  useEffect(() => {
    if (wallet === null && pathname !== '/overview') {
      // setError("Please connect your wallet");
    } else if (connectedNetwork === 'mainnet') {
      setError(`Your wallet address and network selection don't match. ${TESTNET_GUIDE_TEXT}`);
      client.disconnect();
    } else {
      setError(null);
    }
  }, [selectedNetwork, connectedNetwork, wallet]); // FIXME: should add pathname to deps array or remove pathname !== '/overview' or combine if & else if

  return (
    <>
      <header className='fixed top-0 left-0 lg:left-[264px] w-full lg:w-[calc(100%-264px)] h-[74px] lg:h-[94px] flex items-center justify-between px-6 lg:px-8 py-4 lg:py-6 border-b lg:border-b-2 border-border-primary flex-wrap gap-4 bg-gray-700 z-40'>
        <h1 className='hidden lg:block text-text-primary text-2xl leading-7 tracking-02em'>
          {getPageName(pathname)} {isConnected && hasVault && pathname !== '/overview' && <DataTag tag={tag} />}
        </h1>
        <div className='lg:hidden flex gap-2 items-center '>
          <Label pathname={pathname} />
          <DropdownNav pathname={pathname} />
        </div>
        <div className='flex items-center gap-2 lg:gap-5'>
          {!isQuantaRoutes && <BitcoinBanner />}
          <WalletConnect openNetworkSettings={openNetworkSettings} />
          {!isConnected && !isQuantaRoutes && (
            <Button variant='secondary' className='p-1.5 lg:p-2' onClick={openNetworkSettings}>
              <Icons.settings className='h-[18px] w-[18px] lg:h-6 lg:w-6' />
            </Button>
          )}
        </div>
        {(error || walletErrorMessage) && (
          <div className='absolute top-[74px] lg:top-[94px] left-0 flex items-center justify-center w-full p-3 bg-red-100'>
            <p className='text-text-primary font-medium text-lg whitespace-pre-line text-center'>
              {walletErrorMessage ? renderErrorMessage(`${walletErrorMessage} ${TESTNET_GUIDE_TEXT}`) : renderErrorMessage(error || '')}
            </p>
          </div>
        )}
      </header>
      <NetworkSettings isOpen={isNetworkOpen} openNetworkSettings={openNetworkSettings} closeNetworkSettings={closeNetworkSettings} />
    </>
  );
};

export default Header;
